import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PricingTradies = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const plans = [
    {
      name: 'Basic',
      price: 0,
      badge: 'Free',
      badgeColor: 'bg-gray-500',
      features: [
        '3 service listings',
        '3 photos + 1×10s video per listing',
        'Standard messaging',
        'Quote builder (5 line items)',
        'Basic analytics',
      ],
    },
    {
      name: 'Premium',
      price: 3,
      badge: 'Popular',
      badgeColor: 'bg-blue-500',
      features: [
        '10 service listings',
        '8 photos + 1×30s video per listing',
        'Priority support',
        'Quote builder (20 line items)',
        'Review management',
        'Listing analytics',
        'Priority verification',
      ],
    },
    {
      name: 'Pro',
      price: 5,
      badge: 'Best Value',
      badgeColor: 'bg-yellow-500',
      features: [
        '25 service listings',
        '12 photos + 2×30s videos per listing',
        'Sponsored placements',
        'Unlimited quote items',
        'Advanced analytics',
        'Team management (3 seats)',
        'Photo reviews',
        'Lead notifications',
      ],
    },
  ];

  const handleSubscribe = async (planName, price) => {
    if (!user) {
      navigate('/register');
      return;
    }
    if (user.role !== 'tradie') {
      toast.error('Subscriptions are for tradies only');
      return;
    }
    if (price === 0) {
      toast.info('You are on the Basic plan');
      return;
    }
    
    // Redirect to Stripe checkout
    try {
      const origin = window.location.origin;
      const response = await axios.post(`${BACKEND_URL}/api/subscriptions/checkout`, null, {
        params: {
          plan_code: planName.toLowerCase(),
          origin_url: origin
        }
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error('Failed to start checkout');
    }
  };

  return (
    <div className="container mx-auto px-4 py-12" data-testid="pricing-page">
      <div className="text-center mb-12 space-y-4">
        <h1 className="text-4xl font-bold">Choose Your Plan</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Flexible pricing for tradies at every stage. Start free, upgrade anytime.
        </p>
        <div className="inline-block bg-accent-yellow/20 border border-accent-yellow px-4 py-2 rounded-full text-sm font-medium">
          🎉 30-day billing cycle • Cancel anytime
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {plans.map((plan) => (
          <Card
            key={plan.name}
            className={`relative ${
              plan.name === 'Premium' ? 'border-primary border-2 shadow-lg' : ''
            }`}
            data-testid={`plan-${plan.name.toLowerCase()}`}
          >
            {plan.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className={`${plan.badgeColor} text-white`}>{plan.badge}</Badge>
              </div>
            )}
            
            <CardHeader className="text-center pt-8">
              <CardTitle className="text-2xl">{plan.name}</CardTitle>
              <div className="mt-4">
                <span className="text-4xl font-bold">${plan.price}</span>
                <span className="text-muted-foreground">/30 days</span>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <ul className="space-y-3">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button
                onClick={() => handleSubscribe(plan.name, plan.price)}
                className={`w-full rounded-xl ${
                  plan.price === 0
                    ? 'bg-muted hover:bg-muted/80 text-foreground'
                    : plan.name === 'Premium'
                    ? 'bg-primary hover:bg-primary-hover'
                    : 'bg-accent-yellow text-accent-yellow-foreground hover:opacity-90'
                }`}
                disabled={plan.price === 0 && user?.role === 'tradie'}
                data-testid={`subscribe-${plan.name.toLowerCase()}-button`}
              >
                {plan.price === 0 ? 'Current Plan' : `Subscribe to ${plan.name}`}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PricingTradies;
